import { Box, Option } from "@mui/joy";
import DarkSelect from "../../../common/DarkSelect";
import DarkInput from "../../../common/DarkInput";
import IconButtonCTA from "../../../common/IconButtonCTA";
import {
  SCreate,
  SDelete,
  SSave,
  ControlPointDuplicateOutlinedIcon as ControlPointDuplicateOutlined,
} from "../../../../styles/StyledMUI";
import type { MailTemplate, MailTemplateType } from "api/mailTemplates/MailTemplate.typed";
import { MAIL_TEMPLATE_TYPE_OPTIONS } from "api/mailTemplates/MailTemplate.typed";
import { mailEditorToolbarRowSx } from "../mailEditorShared";

type MailEditorToolbarProps = {
  typeFilter: MailTemplateType;
  onTypeFilterChange: (t: MailTemplateType) => void;
  templates: MailTemplate[];
  template: MailTemplate | undefined;
  onTemplateChange: (next: MailTemplate) => void;
  loading: boolean;
  saving: boolean;
  canEdit: boolean;
  onSelectTemplateId: (id: number) => void;
  onNewTemplate: () => void;
  onSave: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
};

const toolbarGridSx = {
  ...mailEditorToolbarRowSx,
  display: "grid",
  gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) minmax(160px, 280px) minmax(0, 1fr)" },
  alignItems: "center",
  columnGap: 1,
  rowGap: 1,
  flexWrap: "nowrap",
} as const;

const toolbarClusterSx = {
  display: "flex",
  flexWrap: "wrap" as const,
  alignItems: "center",
  gap: 1,
  minWidth: 0,
} as const;

export default function MailEditorToolbar(props: MailEditorToolbarProps) {
  const {
    typeFilter,
    onTypeFilterChange,
    templates,
    template,
    onTemplateChange,
    loading,
    saving,
    canEdit,
    onSelectTemplateId,
    onNewTemplate,
    onSave,
    onDuplicate,
    onDelete,
  } = props;

  const showDraft = template != null && template.id == null;
  const selectValue = template?.id != null ? String(template.id) : showDraft ? "draft" : "";

  return (
    <Box sx={toolbarGridSx}>
      <Box sx={toolbarClusterSx}>
        <Box
          sx={{
            minWidth: 130,
            maxWidth: { xs: "100%", sm: 180 },
            flex: { xs: "1 1 120px", sm: "0 0 auto" },
          }}
        >
          <DarkSelect
            value={typeFilter}
            disabled={loading || saving}
            onChange={(_e, val) => onTypeFilterChange((val as MailTemplateType) || "training")}
          >
            {MAIL_TEMPLATE_TYPE_OPTIONS.map((opt) => (
              <Option key={opt.value} value={opt.value}>
                {opt.label}
              </Option>
            ))}
          </DarkSelect>
        </Box>

        <Box
          sx={{
            minWidth: 160,
            maxWidth: { xs: "100%", sm: 280 },
            flex: { xs: "1 1 160px", sm: "1 1 180px" },
          }}
        >
          <DarkSelect
            value={selectValue || null}
            placeholder="Aucun modèle"
            disabled={loading}
            onChange={(_e, val) => {
              const v = String(val || "");
              if (!v || v === "draft") return;
              onSelectTemplateId(Number(v));
            }}
          >
            {showDraft && <Option value="draft">Nouveau modèle</Option>}
            {templates.map((tpl) => (
              <Option key={tpl.id} value={String(tpl.id)}>
                {tpl.name || `Modèle #${tpl.id}`}
              </Option>
            ))}
          </DarkSelect>
        </Box>
      </Box>

      <Box
        sx={{
          width: "100%",
          minWidth: 0,
          justifySelf: "center",
        }}
      >
        {template ? (
          <DarkInput
            value={template.name}
            disabled={!canEdit}
            placeholder="Nom du modèle"
            aria-label="Nom du modèle"
            onChange={(e) => onTemplateChange({ ...template, name: e.target.value })}
            sx={{
              width: "100%",
              "& input": { textAlign: "center" },
            }}
          />
        ) : null}
      </Box>

      <Box sx={{ ...toolbarClusterSx, justifyContent: { xs: "flex-start", md: "flex-end" } }}>
        {canEdit && (
          <>
            <IconButtonCTA
              aria-label="Nouveau modèle e-mail"
              size="sm"
              ctaVariant="accent"
              disabled={loading || saving}
              onClick={onNewTemplate}
            >
              <SCreate />
            </IconButtonCTA>
            {template?.id != null && (
              <IconButtonCTA
                aria-label="Dupliquer le modèle"
                size="sm"
                ctaVariant="accent"
                disabled={loading || saving}
                onClick={() => void onDuplicate()}
              >
                <ControlPointDuplicateOutlined fontSize="small" />
              </IconButtonCTA>
            )}
            {template?.id != null && (
              <IconButtonCTA
                aria-label="Supprimer le modèle"
                size="sm"
                ctaVariant="danger"
                disabled={loading || saving}
                onClick={() => void onDelete()}
              >
                <SDelete />
              </IconButtonCTA>
            )}
            {template && (
              <IconButtonCTA
                aria-label={template.id ? "Enregistrer le modèle" : "Créer le modèle"}
                ctaVariant="success"
                disabled={loading || saving}
                onClick={() => void onSave()}
              >
                <SSave />
              </IconButtonCTA>
            )}
          </>
        )}
      </Box>
    </Box>
  );
}
